"use client";

import { BookOpen, FileText, Send, TrendingUp, Users, Siren, Heart, Target, Sliders, LucideIcon, Check } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { deepDives as data } from "@/lib/data/platform-capabilities";

const iconMap: Record<string, LucideIcon> = {
  BookOpen, FileText, Send, TrendingUp, Users, Siren, Heart, Target, Sliders,
};

export function DeepDives() {
  return (
    <>
      {data.map((dd, i) => {
        const Icon = iconMap[dd.icon] ?? BookOpen;
        const reverse = i % 2 === 1;
        return (
          <Section key={dd.number} id={`capability-${dd.number}`} className="relative">
            <div
              className="pointer-events-none absolute inset-0 -z-10"
              aria-hidden
            >
              <div
                className={`absolute ${reverse ? "left-1/4" : "right-1/4"} top-1/3 h-80 w-80 rounded-full opacity-15 blur-[120px]`}
                style={{ background: `var(--color-${dd.accent})` }}
              />
            </div>

            <Container>
              <Reveal>
                <div className={`grid lg:grid-cols-2 gap-10 items-start ${reverse ? "lg:flex-row-reverse" : ""}`}>
                  <div>
                    <div className="flex items-center gap-3">
                      <span
                        className="grid place-items-center h-14 w-14 rounded-2xl shrink-0"
                        style={{
                          background: `color-mix(in oklch, var(--color-${dd.accent}) 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${dd.accent}) 35%, transparent)`,
                        }}
                      >
                        <Icon className="h-6 w-6" style={{ color: `var(--color-${dd.accent})` }} />
                      </span>
                      <div>
                        <div
                          className="eyebrow-tiny font-bold"
                          style={{ color: `var(--color-${dd.accent})` }}
                        >
                          Capability {dd.number}
                        </div>
                        <h2 className="mt-1">{dd.name}</h2>
                      </div>
                    </div>
                    <p className="mt-5 text-lg font-medium text-[rgb(var(--fg))] leading-snug">
                      {dd.hook}
                    </p>
                    <p className="mt-4 text-body-sm text-[rgb(var(--fg-muted))] leading-relaxed">
                      {dd.intro}
                    </p>
                    <div className="mt-5">
                      <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mb-2">How It Works</div>
                      <p className="text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{dd.howItWorks}</p>
                    </div>
                  </div>

                  <Reveal delay={120}>
                    <div className="space-y-4">
                      {dd.bullets.map((b, j) => (
                        <div
                          key={b.title}
                          className="glass rounded-2xl p-5"
                          style={{
                            boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${dd.accent}) 12%, transparent)`,
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="grid place-items-center h-6 w-6 rounded-full text-[10px] font-bold shrink-0"
                              style={{
                                background: `color-mix(in oklch, var(--color-${dd.accent}) 18%, transparent)`,
                                color: `var(--color-${dd.accent})`,
                              }}
                            >
                              {j + 1}
                            </span>
                            <h3 className="text-sm font-bold">{b.title}</h3>
                          </div>
                          <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{b.body}</p>
                        </div>
                      ))}

                      <div
                        className="rounded-2xl p-5"
                        style={{
                          background: `color-mix(in oklch, var(--color-${dd.accent}) 6%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${dd.accent}) 20%, transparent)`,
                        }}
                      >
                        <div className="eyebrow-tiny mb-3" style={{ color: `var(--color-${dd.accent})` }}>
                          Key Outcomes
                        </div>
                        <ul className="space-y-2">
                          {dd.outcomes.map((o) => (
                            <li key={o} className="flex items-start gap-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">
                              <Check className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: `var(--color-${dd.accent})` }} />
                              <span>{o}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </Reveal>
                </div>
              </Reveal>
            </Container>
          </Section>
        );
      })}
    </>
  );
}
